import { useContext, useMemo } from 'react';
import AppContext from '../AppContext';
import { Follower, ChartData, SocialFollower, ServiceArtistModel } from '../types';
import { groupBy, sumBy } from 'lodash';
import { Audience } from '@arelia-types/streaming';
import { stringEqualsCI } from '../utils/helpers/stringEqualsCI';
import { combineChartDataAndSum } from '@arelia-utils/helpers/chartsData';

export function useSocialFollowerArtistFilter(data: SocialFollower[]): SocialFollower[] {
  const { selectedArtist } = useContext(AppContext);
  return useMemo(() => groupSocialFollowerByNetworkName(filterByArtist(data, selectedArtist)), [data, selectedArtist]);
}

export function useFollowerArtistFilter(data: Follower[]): Follower[] {
  const { selectedArtist } = useContext(AppContext);
  return useMemo(() => groupFollowerByNetworkName(filterByArtist(data, selectedArtist)), [data, selectedArtist]);
}

export function useChartDataArtistFilter(data: ChartData[] | undefined) {
  const { selectedArtist } = useContext(AppContext);
  return useMemo(() => data ? groupChartDataByNetworkName(filterByArtist(data, selectedArtist)) : [], [data, selectedArtist]);
}

export function useArtistFilter<T extends ServiceArtistModel>(data: T[] | undefined) {
  const { selectedArtist } = useContext(AppContext);
  return useMemo(() => data ? filterByArtist(data, selectedArtist) : [], [data, selectedArtist]);
}

export function useAudienceArtistFilter(data: Audience[] | undefined) {
  const { selectedArtist } = useContext(AppContext);
  return useMemo(() => combineAudience(data ? filterByArtist(data, selectedArtist) : []), [data, selectedArtist]);
}

function filterByArtist<T extends ServiceArtistModel>(data: T[], artist: string | null) {
  return artist ? data.filter(x => stringEqualsCI(x.account?.name, artist)) : data;
}

function groupSocialFollowerByNetworkName(data: SocialFollower[]) {
  const grouped = groupBy(data, x => x.networkName);

  return Object.entries(grouped).map(([networkName, group]) => ({
    networkName,
    byCountry: sumRecords(group.map(x => x.byCountry)),
    total: sumBy(group, x => x.total),
    totalLastWeek: sumBy(group, x => x.totalLastWeek),
  } as SocialFollower));
}

function groupFollowerByNetworkName(data: Follower[]) {
  const grouped = groupBy(data, x => x.networkName);

  return Object.entries(grouped).map(([networkName, group]) => ({
    networkName,
    byCountry: sumRecords(group.map(x => x.byCountry)),
  } as Follower));
}

function groupChartDataByNetworkName<T extends ChartData>(data: T[]): T[] {
  const grouped = groupBy(data, x => x.networkName);

  return Object.entries(grouped).map(([networkName, group]) => ({
    networkName,
    ...combineChartDataAndSum(group)
  }) as T);
}

function combineAudience(data: Audience[]) {
  return {
    age: sumRecords(data.map(x => x.age)),
    gender: sumRecords(data.map(x => x.gender)),
  } as Audience;
}

function sumRecords(records: Record<string, number>[]) {
  return records.reduce((a, x) => {
    if (!x)
      return a;

    for (const [k, v] of Object.entries(x)) {
      a[k] = (a[k] ?? 0) + v;
    }

    return a;
  }, {})
}